import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { toast } from "sonner";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { PublicNav } from "@/components/PublicNav";
import { PublicFooter } from "@/components/PublicFooter";
import { logIncident } from "@/lib/incident-log";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in — VISIKT" },
      {
        name: "description",
        content:
          "Sign in to VISIKT to track your subscriptions, salary growth, and real purchasing power.",
      },
      { property: "og:title", content: "Sign in — VISIKT" },
      {
        property: "og:description",
        content:
          "Sign in to VISIKT to track your subscriptions, salary growth, and real purchasing power.",
      },
      { property: "og:url", content: "/auth" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "/auth" }],
  }),
  component: AuthPage,
});

function AuthPage() {
  const { t } = useTranslation();
  const emailSchema = z.string().trim().email(t("auth.invalidEmail")).max(255);
  const passwordSchema = z.string().min(8, t("auth.passwordMin")).max(128);
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "forgot">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/dashboard", replace: true });
    });
  }, [navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        const parsed = emailSchema.safeParse(email);
        if (!parsed.success) {
          toast.error(parsed.error.issues[0].message);
          return;
        }
        const { error } = await supabase.auth.resetPasswordForEmail(parsed.data, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
        toast.success(t("auth.resetSent"));
        setMode("signin");
        return;
      }

      const e2 = emailSchema.safeParse(email);
      const p2 = passwordSchema.safeParse(password);
      if (!e2.success) {
        toast.error(e2.error.issues[0].message);
        return;
      }
      if (!p2.success) {
        toast.error(p2.error.issues[0].message);
        return;
      }

      const { error } = await supabase.auth.signInWithPassword({
        email: e2.data,
        password: p2.data,
      });
      if (error) throw error;
      navigate({ to: "/dashboard", replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("common.somethingWrong");
      toast.error(msg);
      logIncident({
        severity: "warn",
        source: "auth",
        event_type: mode === "forgot" ? "password_reset_failed" : "sign_in_failed",
        message: msg,
        metadata: { email_domain: email.split("@")[1] ?? null },
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogle() {
    setLoading(true);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: window.location.origin,
    });
    if (result.error) {
      toast.error(t("auth.googleFailed"));
      logIncident({
        severity: "warn",
        source: "auth",
        event_type: "oauth_google_failed",
        message: result.error?.message ?? "Google OAuth failed",
      });
      setLoading(false);
      return;
    }
    if (result.redirected) return;
    navigate({ to: "/dashboard", replace: true });
  }

  async function handleApple() {
    setLoading(true);
    const result = await lovable.auth.signInWithOAuth("apple", {
      redirect_uri: window.location.origin,
    });
    if (result.error) {
      toast.error(t("auth.appleFailed"));
      logIncident({
        severity: "warn",
        source: "auth",
        event_type: "oauth_apple_failed",
        message: result.error?.message ?? "Apple OAuth failed",
      });
      setLoading(false);
      return;
    }
    if (result.redirected) return;
    navigate({ to: "/dashboard", replace: true });
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-5 pt-8 pb-12 max-w-md mx-auto w-full flex items-center justify-between gap-3">
        <Link to="/" className="font-display font-extrabold tracking-tighter text-xl">
          VISIKT
        </Link>
        <PublicNav />
      </header>

      <main className="flex-1 px-5 max-w-md mx-auto w-full">
        <div className="mb-8 animate-entry">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            {mode === "forgot" ? t("auth.forgot") : t("auth.welcomeBack")}
          </p>
          <h1 className="text-3xl font-display font-bold tracking-tight">
            {mode === "forgot" ? t("auth.forgotHeadline") : t("auth.signInHeadline")}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {mode === "forgot" ? t("auth.forgotSubtitle") : t("auth.subtitle")}
          </p>
        </div>

        {mode !== "forgot" && (
          <>
            <button
              type="button"
              onClick={handleGoogle}
              disabled={loading}
              className="w-full mb-2 py-3 rounded-md bg-foreground text-background font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-opacity"
            >
              <GoogleIcon /> {t("auth.continueGoogle")}
            </button>
            <button
              type="button"
              onClick={handleApple}
              disabled={loading}
              className="w-full mb-3 py-3 rounded-md bg-foreground text-background font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-opacity"
            >
              <AppleIcon /> {t("auth.continueApple")}
            </button>
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                {t("auth.or")}
              </span>
              <div className="flex-1 h-px bg-border" />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <Field label={t("auth.email")}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              maxLength={255}
              autoComplete="email"
              className="input-base"
            />
          </Field>
          {mode !== "forgot" && (
            <Field label={t("auth.password")}>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("auth.passwordHint")}
                minLength={8}
                maxLength={128}
                autoComplete="current-password"
                className="input-base"
              />
            </Field>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-md bg-accent text-primary font-medium text-sm disabled:opacity-50 transition-opacity"
          >
            {loading
              ? t("auth.waiting")
              : mode === "forgot"
                ? t("auth.submitForgot")
                : t("auth.submitSignIn")}
          </button>
        </form>

        <div className="mt-6 flex flex-col items-center gap-2 text-xs text-muted-foreground">
          {mode === "signin" && (
            <>
              <button onClick={() => setMode("forgot")} className="underline underline-offset-4">
                {t("auth.forgotLink")}
              </button>
              <p>
                {t("auth.noAccount")}{" "}
                <Link to="/register" className="text-foreground underline underline-offset-4">
                  {t("auth.createOne")}
                </Link>
              </p>
            </>
          )}
          {mode === "forgot" && (
            <button
              onClick={() => setMode("signin")}
              className="text-foreground underline underline-offset-4"
            >
              {t("auth.backToSignIn")}
            </button>
          )}
        </div>
      </main>
      <div className="px-5 max-w-md mx-auto w-full">
        <PublicFooter />
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block font-mono text-[9px] uppercase tracking-widest text-muted-foreground mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#fff"
        d="M21.35 11.1H12v2.9h5.35c-.23 1.46-1.66 4.3-5.35 4.3a6.3 6.3 0 1 1 0-12.6c1.78 0 2.97.76 3.65 1.41l2.49-2.4C16.5 3.2 14.45 2.3 12 2.3 6.65 2.3 2.35 6.6 2.35 11.95S6.65 21.6 12 21.6c6.93 0 9.6-4.86 9.6-9.6 0-.65-.07-1.14-.25-1.9Z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#fff"
        d="M16.365 1.43c0 1.14-.42 2.23-1.19 3.05-.79.86-2.05 1.52-3.11 1.43-.13-1.09.42-2.23 1.17-3.02.83-.88 2.24-1.53 3.13-1.46Zm4.02 17.03c-.55 1.27-.82 1.83-1.53 2.95-.99 1.55-2.39 3.48-4.12 3.49-1.55.02-1.95-1.01-4.05-1-2.1.01-2.54 1.02-4.09 1-1.73-.02-3.06-1.76-4.05-3.31C-.68 18.06-.98 12.9 1.87 10.13c1.02-.99 2.41-1.62 3.87-1.65 1.6-.03 3.11 1.08 4.09 1.08.98 0 2.82-1.33 4.75-1.14.81.03 3.08.33 4.54 2.46-3.9 2.14-3.28 7.71 1.27 9.58Z"
      />
    </svg>
  );
}

// Tailwind utility for inputs is defined inline here to keep design tokens consistent.
const _styleTag = `.input-base{display:block;width:100%;padding:0.75rem 0.875rem;border-radius:0.375rem;background-color:transparent;border:1px solid var(--color-border);font-size:0.875rem;color:var(--color-foreground);outline:none;transition:border-color .15s ease;}
.input-base:focus{border-color:var(--color-accent);box-shadow:0 0 0 3px var(--color-ring);}`;
if (typeof document !== "undefined" && !document.getElementById("visikt-input-style")) {
  const s = document.createElement("style");
  s.id = "visikt-input-style";
  s.textContent = _styleTag;
  document.head.appendChild(s);
}
